import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { issueTypeService, type IssueTypeRow } from "@/features/issue-type/services/issueTypeService";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { Modal } from "@/shared/components/ui/Modal";
import { Spinner } from "@/shared/components/ui/Spinner";
import { Toast } from "@/shared/components/feedback/Toast";

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
const generateCode = (existing: string[]): string => {
  let code: string;
  do {
    code = Array.from({ length: 4 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join("");
  } while (existing.includes(code));
  return code;
};

interface FormState {
  code: string;
  descr_en: string;
  descr_ja: string;
  sort_order: string;
  active: boolean;
}

const INIT_FORM: FormState = {
  code: "",
  descr_en: "",
  descr_ja: "",
  sort_order: "0",
  active: true,
};

export const IssueTypePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isOfficer, canWrite } = usePermissions();
  const canEdit = canWrite("visa");

  const [records, setRecords] = useState<IssueTypeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return records;
    return records.filter(
      (r) =>
        r.code?.toLowerCase().includes(q) ||
        r.descr?.en?.toLowerCase().includes(q) ||
        r.descr?.ja?.toLowerCase().includes(q),
    );
  }, [records, search]);

  // Form modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(INIT_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<IssueTypeRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBlockMsg, setDeleteBlockMsg] = useState("");

  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
  }, []);

  const loadRecords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await issueTypeService.getAll();
      setRecords(data);
    } catch {
      showToast(t("issue_type.load_error"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    if (isOfficer) {
      loadRecords();
    } else {
      setLoading(false);
    }
  }, [isOfficer, loadRecords]);

  const openCreate = () => {
    setEditingCode(null);
    const existingCodes = records.map((r) => r.code).filter(Boolean) as string[];
    setForm({ ...INIT_FORM, code: generateCode(existingCodes) });
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row: IssueTypeRow) => {
    setEditingCode(row.code);
    setForm({
      code: row.code,
      descr_en: row.descr?.en ?? "",
      descr_ja: row.descr?.ja ?? "",
      sort_order: String(row.sort_order ?? 0),
      active: row.active ?? true,
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!saving) setModalOpen(false);
  };

  const handleSave = async () => {
    setFormError("");

    if (!form.descr_en.trim() || !form.descr_ja.trim()) {
      setFormError(t("issue_type.descr_required"));
      return;
    }

    const payload = {
      code: form.code,
      descr: {
        en: form.descr_en.trim(),
        ja: form.descr_ja.trim(),
      },
      sort_order: parseInt(form.sort_order, 10) || 0,
      active: form.active,
    };

    setSaving(true);
    try {
      if (editingCode) {
        await issueTypeService.update(editingCode, payload);
      } else {
        await issueTypeService.create(payload);
      }
      showToast(t("issue_type.save_success"), "success");
      setModalOpen(false);
      await loadRecords();
    } catch {
      setFormError(t("issue_type.save_error"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await issueTypeService.delete(deleteTarget.code);
      showToast(t("issue_type.delete_success"), "success");
      setDeleteTarget(null);
      await loadRecords();
    } catch (err: unknown) {
      const errCode = (err as { response?: { data?: { error_code?: string } } })?.response?.data?.error_code;
      if (errCode === "issue_type_in_use") {
        setDeleteBlockMsg(t("issue_type.delete_blocked"));
      } else {
        showToast(t("issue_type.delete_error"), "error");
        setDeleteTarget(null);
      }
    } finally {
      setDeleting(false);
    }
  };

  // ── Permission guard ───────────────────────────────────────────────────────

  if (!isOfficer) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-sm text-gray-500">{t("common.permission_denied")}</p>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-gray-50 -mx-3 px-3 md:-mx-6 md:px-6 py-3 mb-4 md:mb-6 border-b border-gray-200">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/other-setup")}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg md:text-xl font-bold text-gray-900">{t("issue_type.title")}</h1>
              {!loading && (
                <p className="text-xs text-gray-400">
                  {search.trim()
                    ? `${filtered.length} / ${records.length} ${t("issue_type.count_label")}`
                    : `${records.length} ${t("issue_type.count_label")}`}
                </p>
              )}
            </div>
          </div>
          {canEdit && (
            <Button size="sm" onClick={openCreate}>
              {t("issue_type.new_record")}
            </Button>
          )}
        </div>
      </div>

      {/* List */}
      <div>
        <div className="bg-white rounded-xl border border-gray-200">
          {/* Search bar */}
          {!loading && records.length > 0 && (
            <div className="px-4 md:px-6 py-3 border-b border-gray-100">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("common.search_e")}
                  className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-50"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-10">
              <Spinner />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {t("issue_type.no_records")}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400">
              <svg className="w-10 h-10 mx-auto mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
              </svg>
              {t("issue_type.no_search_results")}
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((row) => (
                <li key={row.code} className="flex items-start gap-3 px-4 md:px-6 py-4">
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 font-mono">{row.code}</p>
                      {row.descr?.en && (
                        <p className="text-sm text-gray-700 truncate">{row.descr.en}</p>
                      )}
                      {(row.sort_order ?? 0) > 0 && (
                        <span className="text-[10px] text-gray-400 font-mono">#{row.sort_order}</span>
                      )}
                    </div>
                    {row.descr?.ja && (
                      <p className="text-xs text-gray-500 mt-0.5">{row.descr.ja}</p>
                    )}
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          row.active
                            ? "bg-green-50 text-green-700"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {row.active ? t("settings.status_active") : t("settings.status_inactive")}
                      </span>
                    </div>
                  </div>
                  {/* Actions */}
                  {canEdit && (
                    <div className="flex items-center gap-2 shrink-0 pt-0.5">
                      <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
                        {t("common.edit")}
                      </Button>
                      <button
                        type="button"
                        onClick={() => { setDeleteBlockMsg(""); setDeleteTarget(row); }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        title={t("common.delete")}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Create / Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingCode ? t("issue_type.edit_record") : t("issue_type.new_record")}
        headerAction={
          <Button size="sm" onClick={handleSave} loading={saving}>
            {t("common.save")}
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Code — read-only, auto-generated */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("issue_type.code")}
            </label>
            <div className="flex items-center gap-2 px-3 py-2 text-sm font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-200 rounded-lg select-none">
              {form.code}
              {!editingCode && (
                <span className="ml-auto text-[10px] font-sans font-normal text-gray-400">
                  {t("issue_type.code_auto")}
                </span>
              )}
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">{t("issue_type.descr")}</p>
            <div className="space-y-2">
              <Input
                label={t("issue_type.descr_en")}
                placeholder={t("issue_type.descr_en_placeholder")}
                value={form.descr_en}
                onChange={(e) => setForm((f) => ({ ...f, descr_en: e.target.value }))}
              />
              <Input
                label={t("issue_type.descr_ja")}
                placeholder={t("issue_type.descr_ja_placeholder")}
                value={form.descr_ja}
                onChange={(e) => setForm((f) => ({ ...f, descr_ja: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label={t("issue_type.sort_order")}
              type="number"
              min={0}
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {t("issue_type.active")}
          </label>

          {formError && <p className="text-sm text-red-600">{formError}</p>}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteTarget}
        onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteBlockMsg(""); } }}
        title={t("common.confirm_delete")}
      >
        <div className="space-y-4">
          {deleteTarget && (
            <p className="text-sm font-semibold text-gray-900 bg-gray-50 rounded-lg px-3 py-2 font-mono">
              {deleteTarget.code}{deleteTarget.descr?.en ? ` — ${deleteTarget.descr.en}` : ""}
            </p>
          )}
          {deleteBlockMsg ? (
            <p className="text-sm text-red-600">{deleteBlockMsg}</p>
          ) : (
            <p className="text-sm text-gray-600">{t("issue_type.confirm_delete")}</p>
          )}
          <div className="flex gap-3 justify-end pt-1">
            <Button variant="secondary" onClick={() => { setDeleteTarget(null); setDeleteBlockMsg(""); }} disabled={deleting}>
              {t("common.cancel")}
            </Button>
            {!deleteBlockMsg && (
              <Button variant="danger" onClick={handleDelete} loading={deleting}>
                {t("common.delete")}
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};
